import React, { useState, useEffect } from 'react';
import './SettingsPage.css';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { getAuthToken } from '../Utils';
import { getPageTitle } from '../constants';

const SettingsPage: React.FC = () => {
  const { translations } = useOutletContext<{ translations: Record<string, string> }>();

  const [aliasOpen, setAliasOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const [alias, setAlias] = useState('');
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showCurrentPswd, setShowCurrentPswd] = useState(false);
  const [showNewPswd, setShowNewPswd] = useState(false);

  const [aliasMsg, setAliasMsg] = useState<{ text: string; error: boolean } | null>(null);
  const [emailMsg, setEmailMsg] = useState<{ text: string; error: boolean } | null>(null);
  const [emailCodeMsg, setEmailCodeMsg] = useState<{ text: string; error: boolean } | null>(null);
  const [passwordMsg, setPasswordMsg] = useState<{ text: string; error: boolean } | null>(null);

  useEffect(() => {
    document.title = getPageTitle('Settings');
  }, []);

  const getConfig = () => {
    const token = getAuthToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return { headers, withCredentials: true };
  };

  const handleChangeAlias = async () => {
    if (!alias.trim()) return;
    try {
      await axios.put('/api/users', `alias=${encodeURIComponent(alias)}`, {
        ...getConfig(),
        headers: { ...getConfig().headers, 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      setAliasMsg({ text: translations["settings.alias.updated"], error: false });
      setAlias('');
    } catch (err: any) {
      setAliasMsg({ text: `${err.response?.status}: ${err.response?.data?.error || err.message}`, error: true });
    }
  };

  const handleSendCode = async () => {
    if (!email.trim()) return;
    try {
      await axios.post('/api/email/code', `email=${encodeURIComponent(email)}`, {
        ...getConfig(),
        headers: { ...getConfig().headers, 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      setEmailCodeMsg({ text: translations["settings.email.codeSent"], error: false });
    } catch (err: any) {
      setEmailCodeMsg({ text: `${err.response?.data?.error || err.message}`, error: true });
    }
  };

  const handleChangeEmail = async () => {
    if (!email.trim() || !emailCode.trim()) return;
    try {
      await axios.put('/api/users', `email=${encodeURIComponent(email)}&hash=${encodeURIComponent(emailCode)}`, {
        ...getConfig(),
        headers: { ...getConfig().headers, 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      setEmailMsg({ text: translations["settings.email.changed"], error: false });
      setEmail('');
      setEmailCode('');
    } catch (err: any) {
      setEmailMsg({ text: `${err.response?.status}: ${err.response?.data?.error || err.message}`, error: true });
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword.trim() || !newPassword.trim()) return;
    try {
      await axios.put('/api/users', `password=${encodeURIComponent(newPassword)}&hash=${encodeURIComponent(currentPassword)}`, {
        ...getConfig(),
        headers: { ...getConfig().headers, 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      setPasswordMsg({ text: translations["settings.password.changed"], error: false });
      setCurrentPassword('');
      setNewPassword('');
    } catch (err: any) {
      setPasswordMsg({ text: `${err.response?.status}: ${err.response?.data?.error || err.message}`, error: true });
    }
  };

  const Msg: React.FC<{ msg: { text: string; error: boolean } | null }> = ({ msg }) => {
    if (!msg) return null;
    return <div className={`settings-msg ${msg.error ? 'settings-msg-error' : 'settings-msg-success'}`}>{msg.text}</div>;
  };

  return (
    <div className="settings-page">
      <h2>{translations["message.nav.settings"]}</h2>

      <div className="settings-section">
        <a href="/diary" className="settings-link">
          {translations["message.title.private.info"]}
        </a>
      </div>

      <div className="settings-section">
        <a href="/upload" className="settings-link">
          {translations["title.file.upload"]} | Avatar
        </a>
      </div>

      <div className="settings-section">
        <a href="/email-notifications" className="settings-link">
          {translations["settings.email.title"]}
        </a>
      </div>

      <div className="settings-section">
        <div className="settings-header" onClick={() => setAliasOpen(!aliasOpen)}>
          <span>{translations["alias.change"]}</span>
          <span className={`settings-arrow ${aliasOpen ? 'open' : ''}`}>&#9660;</span>
        </div>
        {aliasOpen && (
          <div className="settings-body">
            <Msg msg={aliasMsg} />
            <label>{translations["alias.new"]}</label>
            <input
              className="settings-input"
              type="text"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
            />
            <button className="settings-button" onClick={handleChangeAlias}>
              {translations["change"]}
            </button>
          </div>
        )}
      </div>

      <div className="settings-section">
        <div className="settings-header" onClick={() => setEmailOpen(!emailOpen)}>
          <span>{translations["email.change"]}</span>
          <span className={`settings-arrow ${emailOpen ? 'open' : ''}`}>&#9660;</span>
        </div>
        {emailOpen && (
          <div className="settings-body">
            <Msg msg={emailMsg} />
            <p className="settings-hint">{translations["email.change.code.about"]}</p>
            <label>{translations["user.info.email"]}</label>
            <input
              className="settings-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Msg msg={emailCodeMsg} />
            <button className="settings-button settings-button-secondary" onClick={handleSendCode}>
              {translations["email.get"]}
            </button>
            <p className="settings-hint">{translations["email.get.info"]}</p>
            <label>{translations["code"]}</label>
            <input
              className="settings-input"
              type="text"
              value={emailCode}
              onChange={(e) => setEmailCode(e.target.value)}
            />
            <button className="settings-button" onClick={handleChangeEmail}>
              {translations["change"]}
            </button>
          </div>
        )}
      </div>

      <div className="settings-section">
        <div className="settings-header" onClick={() => setPasswordOpen(!passwordOpen)}>
          <span>{translations["password.change"]}</span>
          <span className={`settings-arrow ${passwordOpen ? 'open' : ''}`}>&#9660;</span>
        </div>
        {passwordOpen && (
          <div className="settings-body">
            <Msg msg={passwordMsg} />
            <label>{translations["password.present"]}</label>
            <div className="settings-password-field">
              <input
                className="settings-input"
                type={showCurrentPswd ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <button className="settings-eye" onClick={() => setShowCurrentPswd(!showCurrentPswd)}>
                {showCurrentPswd ? '👁' : '👁‍🗨'}
              </button>
            </div>
            <label>{translations["password.new.enter"]}</label>
            <div className="settings-password-field">
              <input
                className="settings-input"
                type={showNewPswd ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <button className="settings-eye" onClick={() => setShowNewPswd(!showNewPswd)}>
                {showNewPswd ? '👁' : '👁‍🗨'}
              </button>
            </div>
            <button className="settings-button" onClick={handleChangePassword}>
              {translations["change"]}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;